import { useEffect, useState } from 'react';

import { Alert, Box, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRange } from '@mui/x-date-pickers-pro';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import { roundedHoursOptions } from 'utils/selectOptionsUtils';

import AcInput from 'components/AcInput/AcInput';

import AcSelect from '../AcSelect/AcSelect';
import ActionButton from '../ActionButton/ActionButton';

import { AcDatePickerProps } from './AcDatePicker.types';
import useShortcutsItems from './hooks/useShortcutsItems';

export default function AcDatePicker({
  onApply,
  onClose,
  isHourPicker,
  isHourPickerInDays = false,
  minDate,
  onApplyButtonText = 'Apply',
  disableFuture = true,
  editRangeDatesValues,
  shouldValidateDaysDiff = true,
  isUTC = false,
  maxDaysDiff = 30
}: AcDatePickerProps) {
  const [isHoursAvailable, setIsHoursAvailable] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [hours, setHours] = useState<[string, string]>(['00:00', '23:59']);
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [duration, setDuration] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { shortcutsItems } = useShortcutsItems(isUTC);

  useEffect(() => {
    if (!hours[0] || hours[1]) setDuration(null);
    const fromTime = dayjs(hours[0], 'HH:mm');
    const toTime = dayjs(hours[1], 'HH:mm');
    // Calculate the difference in hours
    if (toTime.isAfter(fromTime)) {
      const diffInHours = toTime.diff(fromTime, 'hour', true);
      setDuration(Math.round(diffInHours));
    } else {
      setDuration(null); // Set duration to null if invalid range
    }
  }, [hours]);

  useEffect(() => {
    if (editRangeDatesValues) {
      setDateRange(editRangeDatesValues); // Set the initial values when editing
    }
  }, [editRangeDatesValues]);

  useEffect(() => {
    const [startDate, endDate] = dateRange;
    const startHour = dayjs(hours[0], 'HH:mm');
    const endHour = dayjs(hours[1], 'HH:mm');

    if (startHour.isAfter(endHour)) {
      setErrorMessage('Start time cannot be before end time.');
      return;
    }
    if (hours[0] === hours[1]) {
      setErrorMessage('An appearance must be with a duration > 0.');
      return;
    }
    if (startDate && endDate) {
      const diffDays = endDate.diff(startDate, 'day');
      if (diffDays > maxDaysDiff && shouldValidateDaysDiff) {
        setErrorMessage(
          `You can't select a time range that exceeds ${maxDaysDiff} days.`
        );
        return;
      }
    }

    setErrorMessage(null);
  }, [hours, dateRange, shouldValidateDaysDiff, maxDaysDiff]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StaticDateRangePicker
        slotProps={{
          shortcuts: {
            items: shortcutsItems
          },
          actionBar: { actions: [] }
        }}
        disableFuture={disableFuture}
        minDate={minDate || undefined}
        timezone={isUTC ? 'UTC' : undefined}
        value={dateRange}
        onChange={(v) => {
          setDateRange(v);

          if ((!!v[0] && !v[1]) || (!!v[1] && !v[0])) {
            setIsApplyDisabled(true);
          } else {
            setIsApplyDisabled(false);
          }

          if (
            (!!v[0] && !v[1]) ||
            (!!v[0] &&
              !!v[1] &&
              v[0].toDate().toString().trim() ===
                v[1].toDate().toString().trim())
          ) {
            setIsHoursAvailable(true);
          } else {
            setIsHoursAvailable(false);
            setHours(['00:00', '20:59']);
          }
        }}
        calendars={2}
      />
      <Stack style={{ width: '100%', background: 'white' }} pb={2} mt={-2}>
        <Box ml={20}>
          <Stack direction="row" alignItems="center" gap={2} mt={3}>
            {isHourPicker && (
              <>
                <AcSelect
                  header="From"
                  disabled={!isHoursAvailable && !isHourPickerInDays}
                  sx={{ width: 100 }}
                  size="small"
                  value={hours[0]}
                  onChange={(e) =>
                    setHours((oldValues) => [
                      e.target.value as string,
                      oldValues[1]
                    ])
                  }
                  items={roundedHoursOptions}
                />
                <Box mt={1.5}>
                  <span style={{ fontSize: 18 }}>—</span>
                </Box>
                <AcSelect
                  header="To"
                  disabled={!isHoursAvailable && !isHourPickerInDays}
                  sx={{ width: 100 }}
                  size="small"
                  value={hours[1]}
                  onChange={(e) =>
                    setHours((oldValues) => [
                      oldValues[0],
                      e.target.value as string
                    ])
                  }
                  items={[
                    ...roundedHoursOptions,
                    { content: '23:59:59', key: '23:59:59', value: '23:59:59' }
                  ]}
                />
                {duration !== null && isHoursAvailable && !disableFuture && (
                  <Grid size={2} sx={{ ml: 2 }}>
                    <AcInput value={duration} header={'Duration'} disabled />
                  </Grid>
                )}
              </>
            )}
          </Stack>
          {errorMessage && (
            <Box mt={2} mb={1}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}
          {!disableFuture && (
            <Grid size={7.6}>
              <AcInput
                header="Notes"
                name="notes"
                placeholder="Type"
                textarea
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0
                  }
                }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          )}
        </Box>
        <Stack direction="row" justifyContent="flex-end" gap={1} pt={1}>
          <ActionButton
            variant="outlined"
            text={'Close'}
            onClick={() => onClose()}
          />
          <ActionButton
            text={onApplyButtonText}
            disabled={
              isApplyDisabled ||
              dayjs(hours[0], 'HH:mm').isAfter(dayjs(hours[1], 'HH:mm')) ||
              !!errorMessage
            }
            onClick={() =>
              onApply(dateRange, isHoursAvailable ? hours : undefined, notes)
            }
          />
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
}
